import { useRecoilValue, useResetRecoilState } from "recoil"
import React, { useEffect, useState } from "react"
// import { useNavigate } from "react-router-dom"
import moment from "moment"
import {
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  Icon,
  MenuItem,
  Pagination,
  Select,
} from "@mui/material"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import translate from "utils/translate"
import MDDatePicker from "components/MDDatePicker"
import MDInput from "components/MDInput"
import MDSnackbar from "components/MDSnackbar"
import { PackageControllerProvider } from "context/packages"

// Service
import * as packageService from "services/packages/package.service"

import { substractDays } from "utils/functions/global"
import { useNavigate } from "react-router-dom"
import { translateAccountName } from "utils/translate/status"
import { packagesRowIdsState } from "layouts/packages/recoil/states"
import PackagePreselectionDataTable from "./DataTable/PackagePreselectionDataTable"
import PackagesPreselectionTable from "./data/PackagesPreselectionTable"

function Packages() {
  const navigate = useNavigate()
  const selectedRowIdsFromStore = useRecoilValue(packagesRowIdsState)
  const resetSelectedRowIds = useResetRecoilState(packagesRowIdsState)
  const [account, setAccount] = useState("-")
  const [isPrinted, setIsPrinted] = useState("-")
  const [companyName, setCompanyName] = useState(0)
  const [statusFilter, setStatusFilter] = useState("-")
  const [startDatePicker, setStartDatePicker] = useState(substractDays())
  const [endDatePicker, setEndDatePicker] = useState(moment().format("YYYY-MM-DD"))
  const [search, setSearch] = useState("")
  const [packageList, setPackageList] = useState([])
  const [backupPackageList, setBackupPackageList] = useState([])
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [packageTotal, setPackageTotal] = useState(0)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const [urlToPrint, setUrlToPrint] = useState("")
  const rowsPerPage = 10

  // Handle start date picker change event
  const handleStartDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setStartDatePicker(formatDate)
  }

  // Handle end date picker change event
  const handleEndDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setEndDatePicker(formatDate)
  }

  // intialize start date and end date
  const intializeDates = () => {
    const startFrom = substractDays()
    const today = moment().format("YYYY-MM-DD")
    setStartDatePicker(startFrom)
    setEndDatePicker(today)
  }

  // Handle search by client names and phone numbers
  const handleSearchChange = async (evt) => {
    try {
      const word = evt.target.value
      setSearch(word)
      if (word.length >= 3) {
        const companyNameEmptyString = companyName === 0 ? "" : companyName
        const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter
        // console.log("statusFilterEmptyString =>", statusFilterEmptyString)
        // unconfirmed packages
        const isConfirmed = false

        const searchPackages = await packageService.findAllPackages(
          companyNameEmptyString,
          statusFilterEmptyString,
          startDatePicker,
          endDatePicker,
          currentPage,
          word,
          isConfirmed
        )

        const allowedStatuses = ["en attente de confirmation", "non confirmé"]
        const filteredPackages = (searchPackages?.data?.packageList || []).filter((pkg) =>
          allowedStatuses?.includes(pkg?.status?.toLowerCase())
        )

        setPackageList(filteredPackages.length ? filteredPackages : backupPackageList)

        setPackageList(searchPackages.data.packageList || backupPackageList)
      } else {
        setPackageList(backupPackageList)
      }
    } catch (error) {
      setPackageList(backupPackageList)
    }
  }

  // Handle open snackbar
  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Handle company event
  const handleChangeCompany = (evt) => {
    const targetValue = evt.target.value
    setCompanyName(targetValue)
  }

  // Handle status event
  const handleChangeStatus = (evt) => {
    const targetValue = evt.target.value
    // console.log("targetValue=>", targetValue)
    setStatusFilter(targetValue)
  }

  // Reset filters
  const resetFilters = () => {
    setCompanyName(0)
    setStartDatePicker("")
    setEndDatePicker("")
    setSearch("")
    setStatusFilter("-")
    setIsPrinted("-")
    setAccount("-")
    intializeDates()
    onResetRowIds()
    getAllPackages()
  }

  // Reset recoil store
  const onResetRowIds = () => {
    resetSelectedRowIds()
    navigate(0)
  }

  // Gel all packages
  const getAllPackages = async () => {
    try {
      setIsDataLoading(true)
      const companyNameEmptyString = companyName === 0 ? "" : companyName
      const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter
      const isPrintedEmptyString = isPrinted === "-" ? "" : isPrinted
      const accountEmptyString = account === "-" ? "" : account

      // unconfirmed packages
      const isConfirmed = false

      const { data } = await packageService.findAllPackages(
        companyNameEmptyString,
        statusFilterEmptyString,
        startDatePicker,
        endDatePicker,
        currentPage,
        search,
        isPrintedEmptyString,
        accountEmptyString,
        isConfirmed
      )

      const allowedStatuses = ["en attente de confirmation", "non confirmé"]
      const filteredPackages = (data?.packageList || []).filter((pkg) =>
        allowedStatuses?.includes(pkg?.status?.toLowerCase())
      )

      setPackageList(filteredPackages)
      setBackupPackageList(filteredPackages)
      setPackageTotal(data?.packageTotal)
      setIsDataLoading(false)
    } catch (error) {
      setPackageList([])
    }
  }

  // Handle pagination change
  const handleChangePagination = (event, value) => {
    setCurrentPage(value)
  }

  // Calculate number of pages
  const pageCount = (total) => {
    if (total <= rowsPerPage) return rowsPerPage
    let result = total / rowsPerPage
    if (result % 1 !== 0) result = Math.floor(result) + 1
    return result
  }

  // Refresh all status
  const refreshMissingPackages = async () => {
    setIsLoadingPDF(true)
    try {
      const packageInstance = await packageService.refreshMissingPackages()
      if (packageInstance.status === 200) {
        handleOpenSnackbar("success", "Tous les colis manquants ont été rafraichis", "check_circle")
        setIsLoadingPDF(false)
        await getAllPackages()
      }
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle account event
  const handleChangeAccount = (evt) => {
    const targetValue = evt.target.value
    setAccount(targetValue)
  }

  // Handle row id
  const getRowId = (row, relativeIndex, parent) => {
    const barcode = row.barcode.props.children
    return parent ? [parent.id, barcode].join(".") : barcode
  }

  useEffect(() => {
    getAllPackages()
    return () => {
      if (window.location.pathname !== "/colis" && window.location.pathname !== "/pre-selection-commandes") {
        onResetRowIds()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatePicker, endDatePicker, companyName, statusFilter, currentPage, search, isPrinted, account])

  const handleOpenPdf = (url) => {
    window.open(url, "_blank")
    setUrlToPrint("")
    onResetRowIds()
  }

  return (
    <PackageControllerProvider>
      <DashboardLayout>
        <DashboardNavbar absolute />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <MDTypography mt={2} gutterBottom variant="body1">
                    Filtrage des commandes
                  </MDTypography>
                  <MDBox>
                    <Grid container display="flex" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Grid item xs={12} lg={2}>
                        <MDInput fullWidth label="Cherche ici..." value={search} onChange={handleSearchChange} />
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-company_id-label"
                            id="id-filter-company_id"
                            value={companyName}
                            onChange={handleChangeCompany}
                            label="Company"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value={0}>
                              <em>Toutes les entreprises</em>
                            </MenuItem>
                            <MenuItem value="alamana">Alamana</MenuItem>
                            <MenuItem value="lazajella">Lazajella</MenuItem>
                            <MenuItem value="mylerz">Mylerz</MenuItem>
                            <MenuItem value="chahin">Chahin</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-accounts-label"
                            id="id-filter-accounts"
                            value={account}
                            onChange={handleChangeAccount}
                            label="Account"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tout sélectionner</em>
                            </MenuItem>
                            <MenuItem value="account1">{translateAccountName("account1")}</MenuItem>
                            {companyName !== "alamana" && (
                              <MenuItem value="account2">{translateAccountName("account2")}</MenuItem>
                            )}
                            <MenuItem value="account3">{translateAccountName("account3")}</MenuItem>
                            <MenuItem value="account4">{translateAccountName("account4")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-status-label"
                            id="id-filter-status"
                            value={statusFilter}
                            onChange={handleChangeStatus}
                            label="Status"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tous les statuts</em>
                            </MenuItem>
                            <MenuItem value="en attente de confirmation">En attente de confirmation</MenuItem>
                            <MenuItem value="non confirmé">Non confirmé</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleStartDatePicker}
                          value={startDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de début",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleEndDatePicker}
                          value={endDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de fin",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDTypography variant="h6" color="white">
                    Gestion des commandes
                  </MDTypography>
                  <MDTypography variant="h6" color="white">
                    Nombre des commandes sélectionné ({Object.keys(selectedRowIdsFromStore).length})
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  {isDataLoading ? (
                    <MDBox
                      p={26}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MDTypography component="p" variant="body2">
                        Loading...
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <PackagePreselectionDataTable
                      table={PackagesPreselectionTable(packageList)}
                      isSorted={false}
                      noEndBorder={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      getRowId={getRowId}
                      onResetRowIds={() => onResetRowIds()}
                    />
                  )}
                  <Grid container p={3} pb={1}>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={refreshMissingPackages}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "hourglass_top"}</Icon>
                        &nbsp;actualiser tous les colis manquants
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={resetFilters}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "restart_alt"}</Icon>
                        &nbsp;réinitialiser tous les filtres
                      </MDButton>
                    </Grid>
                    {urlToPrint !== "" && (
                      <Grid item>
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="warning"
                          onClick={() => handleOpenPdf(urlToPrint)}
                        >
                          Ouvrir les colis pour imprimer
                        </MDButton>
                      </Grid>
                    )}
                  </Grid>
                  {packageTotal > 10 && (
                    <Grid p={3} pt={0} container alignItems="center" justifyContent="space-between">
                      <Grid item sm={12} md={6} pt={3}>
                        <MDTypography component="p" variant="body2">
                          Affichage {currentPage} à {rowsPerPage} sur {packageTotal} colis
                        </MDTypography>
                      </Grid>
                      <Grid item pt={3}>
                        <Pagination
                          count={pageCount(packageTotal)}
                          defaultPage={currentPage}
                          page={currentPage}
                          onChange={handleChangePagination}
                          color="info"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Colis"
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
        sx={{
          float: "left",
        }}
      />
    </PackageControllerProvider>
  )
}

export default Packages
